import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/20/solid';

import { useAuth } from '../../hooks/use-auth';
import * as languagesService from '../../services/api/languages';
import * as translationsService from '../../services/api/translations';
import { Language } from '../../entities/language';
import TranslationForm from '../../entities/form/translation';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import CommonButtonDefault from '../../components/common/buttons/default';
import CommonConfirmationModal from '../../components/modals/confirmation';
import Heading1 from '../../components/common/headings/heading1';
import LabelWithError from '../../components/common/form/label-with-error';
import StyledInput from '../../components/common/form/styled-input';
import { strings } from '../../localization/strings';

interface ComponentProps {
  isEdit?: boolean;
}

const cardClassName = 'rounded-lg bg-white ring-1 ring-gray-900/5';

const textForLanguages = (languages: Language[], text?: Record<string, string | null>) =>
  languages.reduce<Record<string, string | null>>(
    (values, language) => ({ ...values, [language.key]: text?.[language.key] || null }),
    {},
  );

function TranslationsEditPageComponent({ isEdit }: ComponentProps) {
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<TranslationForm>({
    defaultValues: {
      key: '',
      group: '*',
      text: {},
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const activeLanguages = await languagesService.list(token);

        setLanguages(activeLanguages);

        if (isEdit && id) {
          const item = await translationsService.one(token, id);

          reset({
            key: item.key,
            group: item.group || '*',
            text: textForLanguages(activeLanguages, item.text),
          });
        } else {
          reset({
            key: '',
            group: '*',
            text: textForLanguages(activeLanguages),
          });
        }

        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setError((e as Error).message);
      }
    };

    fetchData();
  }, [id, isEdit, reset, token]);

  const onSubmit = async (data: TranslationForm) => {
    try {
      setIsLoading(true);
      if (isEdit && id) {
        await translationsService.update(token, parseInt(id, 10), data);
      } else {
        await translationsService.create(token, data);
      }

      navigate('/translations?success=1');
    } catch (e) {
      setIsLoading(false);
      setError((e as Error).message);
    }
  };

  const onDelete = async () => {
    if (!id) {
      return;
    }

    try {
      setIsLoading(true);
      await translationsService.remove(token, parseInt(id, 10));
      navigate('/translations');
    } catch (e) {
      setIsLoading(false);
      setError((e as Error).message);
    }
  };

  return (
    <div>
      {isLoading && <CommonAnimatedLoader />}
      {!isLoading && (
        <>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <a href="/" className="text-gray-400 hover:text-gray-500">
                  <HomeIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                  <span className="sr-only">{strings.common.home}</span>
                </a>
              </li>
              <li>
                <div className="flex items-center">
                  <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  <Link to="/translations" className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                    {strings.navigation.translations}
                  </Link>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  <span className="ml-4 text-sm font-medium text-gray-500" aria-current="page">
                    {isEdit ? strings.translations.editTitle : strings.translations.createTitle}
                  </span>
                </div>
              </li>
            </ol>
          </nav>
          <header className="mb-3 mt-3">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Heading1>{isEdit ? strings.translations.editTitle : strings.translations.createTitle}</Heading1>
            </div>
          </header>
          <main>
            <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
              {error && (
                <div className="mb-4">
                  <AlertPrimary type="danger" text={error} />
                </div>
              )}
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-6">
                  <section className={cardClassName}>
                    <div className="grid gap-x-6 gap-y-6 p-4 grid-cols-12">
                      <div className="col-span-12">
                        <LabelWithError title={strings.translations.form.key} error={errors.key?.message} hasStar>
                          <StyledInput
                            type="text"
                            {...register('key', { required: strings.translations.form.keyRequired })}
                            hasError={!!errors.key}
                          />
                        </LabelWithError>
                      </div>

                      {languages.map((language) => (
                        <div key={language.id} className="col-span-12">
                          <LabelWithError title={language.name} error={undefined}>
                            <StyledInput type="text" {...register(`text.${language.key}` as never)} />
                          </LabelWithError>
                        </div>
                      ))}
                    </div>
                  </section>

                  <div className="flex justify-end space-x-4 px-1 pb-2 pt-2">
                    {isEdit && (
                      <CommonButtonDefault type="button" danger onClick={() => setConfirmDelete(true)}>
                        {strings.button.delete}
                      </CommonButtonDefault>
                    )}
                    <CommonButtonDefault type="button" secondary onClick={() => navigate('/translations')}>
                      {strings.button.cancel}
                    </CommonButtonDefault>
                    <CommonButtonDefault type="submit" primary>
                      {strings.button.save}
                    </CommonButtonDefault>
                  </div>
                </div>
              </form>
            </div>
          </main>
          <CommonConfirmationModal
            open={confirmDelete}
            cancelled={() => setConfirmDelete(false)}
            confirmed={onDelete}
            danger
            title={strings.common.confirmation.deleteTranslation.title}
            description={strings.common.confirmation.deleteTranslation.description}
            buttonConfirm={strings.button.delete}
            buttonCancel={strings.button.cancel}
          />
        </>
      )}
    </div>
  );
}

TranslationsEditPageComponent.defaultProps = {
  isEdit: false,
};

export default TranslationsEditPageComponent;
